import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { participantFragment } from '../fragments/commonData.fragments';
import { errorToast } from 'basics/utils/toast';
import { Participant, RegisterParticipantInput } from 'generated/event.types';
import { ErrorMessage } from 'modules/events/basics/enums/events.enums';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type UseAddParticipantMutationResponse = {
  registerParticipant: Participant;
};

export type UseAddParticipantMutation = [
  (participant: RegisterParticipantInput, timeZone: string) => Promise<Participant | null>,
  MutationResult<UseAddParticipantMutationResponse>,
];

export const addParticipantMutationCall = gql`
mutation registerParticipant($participant: RegisterParticipantInput!, $timeZone: String!) {
  registerParticipant(participant: $participant, timeZone: $timeZone) {
       ${participantFragment}
      }
}`;

export const useAddParticipantMutation = (): UseAddParticipantMutation => {
  const { t } = useTranslation();
  const [registerInviteeMutation, state] = useMutation<UseAddParticipantMutationResponse>(addParticipantMutationCall);

  const executeMutation = useCallback(async (participant: RegisterParticipantInput, timezone: string) => {
    try {
      const { data } = await registerInviteeMutation({
        variables: { participant, timeZone: timezone },
      });
      if (!data?.registerParticipant) {
        errorToast(t('errors_standard'));
        return null;
      }

      return data?.registerParticipant;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'addParticipant.ts - useAddParticipantMutation',
        });
        if (customContext?.message === ErrorMessage.ZOOM_TOO_MANY_REQUESTS) {
          errorToast(t('error_zoom_too_many_requests'));
        } else {
          errorToast(t('errors_standard'));
        }
      }
      return null;
    }
  }, [registerInviteeMutation, t]);

  return [executeMutation, state];
};
